import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type VenueFilters } from "../../api/client";
import { VenueCard } from "../../components/VenueCard";
import { PageHeader, Skeleton, StatePanel } from "../../components/ui";
import type { VenueSummary } from "../../domain/models";
import { t } from "../../i18n";

export function ExplorePage() {
  const [mode, setMode] = useState<"list" | "map">("list");
  const [filters, setFilters] = useState<VenueFilters>({});
  const query = useQuery({
    queryKey: ["venues", filters],
    queryFn: () => api.searchVenues(filters),
    placeholderData: (previous) => previous,
  });
  return (
    <div className="page">
      <PageHeader eyebrow={t.explore.eyebrow} title={t.explore.title} />
      <p className="page-intro">{t.explore.intro}</p>
      <label className="search-box">
        <span aria-hidden>⌕</span>
        <input
          type="search"
          placeholder={t.explore.search}
          value={filters.query ?? ""}
          onChange={(event) =>
            setFilters({ ...filters, query: event.target.value })
          }
        />
      </label>
      <div className="filter-row">
        <select
          aria-label="Activity category"
          value={filters.category ?? ""}
          onChange={(event) =>
            setFilters({
              ...filters,
              category: event.target.value || undefined,
            })
          }
        >
          <option value="">{t.explore.allTypes}</option>
          {Object.entries(t.categories).map(([code, name]) => (
            <option value={code} key={code}>
              {name}
            </option>
          ))}
        </select>
        <select
          aria-label="Price band"
          value={filters.priceBand ?? ""}
          onChange={(event) =>
            setFilters({
              ...filters,
              priceBand: event.target.value
                ? Number(event.target.value)
                : undefined,
            })
          }
        >
          <option value="">{t.explore.anyPrice}</option>
          <option value="1">$</option>
          <option value="2">$$</option>
          <option value="3">$$$</option>
          <option value="4">$$$$</option>
        </select>
        <button
          className={filters.openNow ? "active" : ""}
          onClick={() => setFilters({ ...filters, openNow: !filters.openNow })}
        >
          {t.explore.openNow}
        </button>
      </div>
      <div className="segmented">
        <button
          className={mode === "list" ? "active" : ""}
          onClick={() => setMode("list")}
        >
          {t.explore.list}
        </button>
        <button
          className={mode === "map" ? "active" : ""}
          onClick={() => setMode("map")}
        >
          {t.explore.map}
        </button>
      </div>
      {query.isLoading ? (
        <>
          {[1, 2, 3].map((index) => (
            <Skeleton key={index} height={210} />
          ))}
        </>
      ) : query.isError ? (
        <StatePanel
          title="Couldn’t load places"
          body="Check your connection and try again."
        />
      ) : query.data?.length === 0 ? (
        <StatePanel
          title="No matching places"
          body="Try removing a filter or searching another area."
        />
      ) : mode === "list" ? (
        <div className="venue-list">
          {query.data?.map((item) => (
            <VenueCard key={item.id} venue={item} />
          ))}
        </div>
      ) : (
        <VenueMap venues={query.data ?? []} />
      )}
    </div>
  );
}
function VenueMap({ venues }: { venues: VenueSummary[] }) {
  return (
    <div className="map-view" aria-label="Place map">
      <div className="map-water" />
      <div className="map-label">BEIRUT</div>
      {venues.map((item, index) => (
        <a
          href={`/venues/${item.id}`}
          key={item.id}
          className={`map-pin pin-${index % 6}`}
          aria-label={`${item.venueName}, ${item.area}`}
        >
          <span>{"$".repeat(item.priceBand)}</span>
        </a>
      ))}
      <div className="map-note">
        Map provider adapter not configured · showing approximate clusters
      </div>
    </div>
  );
}
